/**
 * Institution Slice
 * Manages institution type and feature availability state
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../services/apiClient';

// Async thunks for API calls

/**
 * Fetch current institution settings
 */
export const fetchInstitutionSettings = createAsyncThunk(
  'institution/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/institution/settings');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch settings');
    }
  }
);

/**
 * Update institution type (superadmin only)
 */
export const updateInstitutionType = createAsyncThunk(
  'institution/updateType',
  async (institutionType, { rejectWithValue }) => {
    try {
      const response = await apiClient.put('/institution/settings', {
        institution_type: institutionType
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update institution type');
    }
  }
);

/**
 * Fetch available features for current institution type
 */
export const fetchAvailableFeatures = createAsyncThunk(
  'institution/fetchFeatures',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/institution/features/available');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch features');
    }
  }
);

/**
 * Fetch all institution types with descriptions
 */
export const fetchInstitutionTypes = createAsyncThunk(
  'institution/fetchTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/institution/types');
      // Response data is now wrapped in { types: [...] }
      return response.data.data.types || response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch institution types');
    }
  }
);

// Slice definition
const institutionSlice = createSlice({
  name: 'institution',
  initialState: {
    currentType: null,
    availableTypes: [],
    typesInfo: [],
    availableFeatures: {
      sidebar: []
    },
    settings: null,
    loading: false,
    error: null,
    lastUpdated: null
  },
  reducers: {
    /**
     * Clear error message
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set institution type directly (for manual updates)
     */
    setInstitutionType: (state, action) => {
      state.currentType = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch settings
      .addCase(fetchInstitutionSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInstitutionSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
        state.currentType = action.payload.institution_type;
        state.availableTypes = action.payload.available_types;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchInstitutionSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update type
      .addCase(updateInstitutionType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInstitutionType.fulfilled, (state, action) => {
        state.loading = false;
        state.currentType = action.payload.institution_type;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateInstitutionType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch features
      .addCase(fetchAvailableFeatures.pending, (state) => {
        // Don't set loading for background feature fetch
      })
      .addCase(fetchAvailableFeatures.fulfilled, (state, action) => {
        state.availableFeatures = action.payload.available_features;
      })
      .addCase(fetchAvailableFeatures.rejected, (state, action) => {
        console.warn('Failed to fetch available features:', action.payload);
      })

      // Fetch types info
      .addCase(fetchInstitutionTypes.pending, (state) => {
        // Don't set loading for background types fetch
      })
      .addCase(fetchInstitutionTypes.fulfilled, (state, action) => {
        state.typesInfo = action.payload;
      })
      .addCase(fetchInstitutionTypes.rejected, (state, action) => {
        console.warn('Failed to fetch institution types:', action.payload);
      });
  }
});

// Export actions
export const { clearError, setInstitutionType } = institutionSlice.actions;

// Selectors
export const selectInstitutionType = (state) => state.institution?.currentType;
export const selectAvailableFeatures = (state) => state.institution?.availableFeatures || { sidebar: [] };
export const selectInstitutionSettings = (state) => state.institution?.settings;
export const selectInstitutionLoading = (state) => state.institution?.loading || false;
export const selectInstitutionError = (state) => state.institution?.error;
export const selectTypesInfo = (state) => state.institution?.typesInfo || [];

// Export reducer
export default institutionSlice.reducer;
